import React, { useState, useEffect } from 'react';
import { logAuthFailure } from '../services/PreAuthLogService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createUserWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { auth, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import UserService from '../services/UserService';
const LoginPage = () => {
  const [mode, setMode] = useState('choose'); // 'choose', 'student', 'teacher', 'login', 'register'
  const [studentCode, setStudentCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(() => {
    // Afficher un message si le compte a été révoqué
    if (sessionStorage.getItem('revokedAccount')) {
      sessionStorage.removeItem('revokedAccount');
      return "Votre accès a été révoqué. Pour toute question, contactez Cyril.";
    }
    return '';
  });
  const [loading, setLoading] = useState(false);
  const [teacherCode, setTeacherCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [ageConsent, setAgeConsent] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [notVerifiedEmail, setNotVerifiedEmail] = useState('');
  const { login, loginAnonymously, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const emailJustVerified = new URLSearchParams(location.search).get('verified') === '1';
  const verificationError = new URLSearchParams(location.search).get('verified') === 'error';

  // Changer de formulaire : repartir d'une confirmation vide
  useEffect(() => {
    setConfirmPassword('');
  }, [mode]);

  useEffect(() => {
    if (emailJustVerified) setMode('login');
  }, [emailJustVerified]);

  // Mode anonyme : découverte limitée
  const handleAnonymousSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await loginAnonymously();
      await UserService.createUserProfile(
        userCredential.user.uid,
        null,
        `Visiteur_${Date.now().toString().slice(-6)}`,
        null,
        null,
        'anonymous'
      );
      setTimeout(() => navigate('/'), 500);
    } catch (error) {
      console.error('Erreur inscription anonyme:', error);
      logAuthFailure('signup_anonymous', error);
      setError('Erreur lors de la création du compte. Réessaie !');
    } finally {
      setLoading(false);
    }
  };

  // Mode élève : email réel avec code d'accès
  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Les deux mots de passe ne sont pas identiques');
      return;
    }
    setLoading(true);

    try {
      // Vérifier le code d'accès via Cloud Function (jamais exposé dans le bundle)
      const validateCode = httpsCallable(functions, 'validateAccessCode');
      const result = await validateCode({ code: studentCode, type: 'student' });
      if (!result.data.valid) {
        setError('Code d\'accès incorrect. Demande le code à ton professeur !');
        setLoading(false);
        return;
      }

      // Créer le compte Firebase avec email réel
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Stocker le type de compte pour FirstLoginPage (pseudo/niveau seront demandés là-bas)
      localStorage.setItem(`pendingProfile_${userCredential.user.uid}`, JSON.stringify({
        accountType: 'student'
      }));

      // Envoyer l'email de vérification (session maintenue, accès bloqué jusqu'à vérification)
      await sendEmailVerification(userCredential.user, {
        url: 'https://drumigo.com/login?verified=1'
      });
      navigate('/verify-email');
    } catch (error) {
      console.error('Erreur inscription élève:', error);
      logAuthFailure('signup_student', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Un compte existe déjà avec cette adresse email');
          break;
        case 'auth/invalid-email':
          setError('Adresse email invalide');
          break;
        case 'auth/weak-password':
          setError('Le mot de passe doit contenir au moins 6 caractères');
          break;
        default:
          setError('Erreur lors de la création du compte. Réessaie !');
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Entre ton adresse email pour réinitialiser ton mot de passe.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
    } catch (err) {
      logAuthFailure('reset_send', err);
      setError('Impossible d\'envoyer l\'email. Vérifie l\'adresse saisie.');
    } finally {
      setLoading(false);
    }
  };

  // Mode email : connexion ou inscription classique (prof / register)
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if ((mode === 'register' || mode === 'teacher') && password !== confirmPassword) {
      setError('Les deux mots de passe ne sont pas identiques');
      return;
    }
    setLoading(true);

    try {
      if (mode === 'register' || mode === 'teacher') {
        // Vérification du code d'accès professeur via Cloud Function
        if (mode === 'teacher') {
          const validateCode = httpsCallable(functions, 'validateAccessCode');
          const result = await validateCode({ code: teacherCode, type: 'teacher' });
          if (!result.data.valid) {
            setError('Code d\'accès professeur incorrect. Contactez Cyril pour obtenir votre code !');
            setLoading(false);
            return;
          }
          // Conserver le code validé pour l'étape de création de profil
          sessionStorage.setItem('pendingTeacherAccessCode', teacherCode.trim());
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Stocker le type de compte en localStorage (sessionStorage ne survit pas au clic sur le lien email)
        if (mode === 'teacher') {
          localStorage.setItem(
            `pendingProfile_${userCredential.user.uid}`,
            JSON.stringify({ accountType: 'teacher', teacherCode: teacherCode.trim() })
          );
        }

        // Envoyer l'email de vérification (session maintenue, accès bloqué jusqu'à vérification)
        await sendEmailVerification(userCredential.user, {
          url: 'https://drumigo.com/login?verified=1'
        });
        navigate('/verify-email');
      } else {
        // Mode connexion
        await login(email, password);
        setTimeout(() => navigate('/'), 500);
      }
    } catch (error) {
      console.error('Erreur:', error);
      logAuthFailure(mode === 'register' ? 'signup' : 'login', error);
      switch (error.code) {
        case 'auth/invalid-email':
          setError('Adresse email invalide');
          break;
        case 'auth/user-disabled':
          setError('Ce compte a été désactivé');
          break;
        case 'auth/user-not-found':
          setError('Aucun compte trouvé avec cette adresse email');
          break;
        case 'auth/wrong-password':
          setError('Mot de passe incorrect');
          break;
        case 'auth/invalid-credential':
          setError('Email ou mot de passe incorrect');
          break;
        case 'auth/email-not-verified':
          setNotVerifiedEmail(email);
          setError('Ton adresse email n\'est pas encore vérifiée. Clique sur le lien dans l\'email reçu lors de ton inscription.');
          break;
        case 'auth/email-already-in-use':
          setError('Un compte existe déjà avec cette adresse email');
          break;
        case 'auth/weak-password':
          setError('Le mot de passe doit contenir au moins 6 caractères');
          break;
        default:
          setError(`Erreur ${mode === 'register' ? "d'inscription" : "de connexion"}. Veuillez réessayer.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Champ de confirmation du mot de passe (inscription élève / prof / email).
  // Fonction de rendu et non composant : un composant défini ici serait recréé à chaque frappe (perte de focus).
  const renderConfirmPasswordField = (id, spacing = 'mb-3') => (
    <div className={spacing}>
      <label htmlFor={id} className="block text-slate-300 font-semibold mb-1 text-sm">
        Confirmer le mot de passe
      </label>
      <input
        type={showPassword ? 'text' : 'password'}
        id={id}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        autoComplete="new-password"
        className={`w-full px-4 py-2 bg-slate-900/60 border rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${
          confirmPassword && confirmPassword !== password ? 'border-red-500' : 'border-white/10'
        }`}
        placeholder="••••••••"
        required
        minLength={6}
        disabled={loading}
      />
      {confirmPassword && confirmPassword !== password && (
        <p className="text-xs text-white bg-red-700 rounded-lg px-2 py-1 mt-1">
          Les deux mots de passe ne sont pas identiques
        </p>
      )}
    </div>
  );

  // Composant checkbox de consentement (réutilisé dans élève et prof)
  const AgeConsentCheckbox = () => (
    <div className="mb-3">
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={ageConsent}
          onChange={(e) => setAgeConsent(e.target.checked)}
          required
          disabled={loading}
          className="mt-0.5 w-4 h-4 rounded accent-amber-500 flex-shrink-0 cursor-pointer"
        />
        <span className="text-xs text-slate-300 group-hover:text-amber-50 transition-colors leading-relaxed">
          Je certifie avoir plus de 15 ans, ou avoir obtenu l'accord d'un parent ou tuteur légal pour utiliser cette application.
        </span>
      </label>
    </div>
  );

  // Écran de choix initial
  if (mode === 'choose') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 relative overflow-hidden p-4 pb-10">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-transparent to-slate-950/50 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-amber-500/10 to-transparent pointer-events-none" />
        <div className="bg-slate-800/60 backdrop-blur-xl border border-white/10 p-8 rounded-2xl shadow-2xl w-full max-w-md relative z-10">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-amber-400 font-display mb-2">Drumigo</h1>
            <p className="text-slate-400">Bienvenue !</p>
          </div>

          <div className="space-y-4">
            {/* Connexion — mis en avant en premier */}
            <button
              onClick={() => setMode('login')}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold text-lg rounded-2xl shadow-lg shadow-amber-500/20 transition-all hover:shadow-amber-500/40 hover:shadow-xl"
            >
              🔑 Se connecter
            </button>

            {/* Séparateur nouveaux utilisateurs */}
            <div className="flex items-center gap-3 pt-1">
              <div className="flex-1 h-px bg-white/10" />
              <p className="text-xs text-slate-500 whitespace-nowrap">Nouveau sur Drumigo ?</p>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Mode Élève */}
            <button
              onClick={() => setMode('student')}
              className="w-full p-5 bg-gradient-to-r from-blue-600/80 to-indigo-600/80 hover:from-blue-500 hover:to-indigo-500 border border-blue-500/30 text-white rounded-2xl shadow-lg transition-all hover:shadow-blue-500/20 hover:shadow-xl"
            >
              <div className="text-left">
                <p className="text-xl font-bold mb-1">🎓 Je suis élève</p>
                <p className="text-sm opacity-90">Accès complet avec code prof</p>
                <p className="text-xs opacity-75 mt-1">→ Progression sauvegardée partout</p>
              </div>
            </button>

            {/* Mode Professeur */}
            <button
              onClick={() => setMode('teacher')}
              className="w-full p-5 bg-gradient-to-r from-amber-600/80 to-orange-600/80 hover:from-amber-500 hover:to-orange-500 border border-amber-500/30 text-white rounded-2xl shadow-lg transition-all hover:shadow-amber-500/20 hover:shadow-xl"
            >
              <div className="text-left">
                <p className="text-xl font-bold mb-1">👨‍🏫 Je suis professeur</p>
                <p className="text-sm opacity-90">Gérer mes élèves et suivre leur progression</p>
                <p className="text-xs opacity-75 mt-1">→ Tableau de bord + mode cours</p>
              </div>
            </button>

            {/* Mode Découverte (Anonyme) */}
            <div className="pt-1 border-t border-white/10">
              <button
                onClick={() => handleAnonymousSubmit({ preventDefault: () => {} })}
                disabled={loading}
                className="w-full py-3 bg-slate-700/60 hover:bg-slate-700 border border-white/10 text-slate-300 font-medium rounded-2xl transition-colors text-sm"
              >
                🚀 Essayer sans compte <span className="opacity-60">(chapitre 1 uniquement)</span>
              </button>
            </div>
          </div>
        </div>
        <p className="absolute bottom-4 text-center text-xs text-slate-600 z-10">© 2026 Drumigo. Tous droits réservés.</p>
      </div>
    );
  }

  // Formulaire mode élève
  if (mode === 'student') {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden py-4">
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-amber-500/10 to-transparent pointer-events-none" />
        <div className="bg-slate-800/60 backdrop-blur-xl border border-white/10 p-5 rounded-2xl shadow-2xl w-full max-w-md relative z-10 max-h-full overflow-y-auto">
          <button
            onClick={() => setMode('choose')}
            className="mb-3 text-slate-400 hover:text-amber-50 flex items-center gap-2 transition-colors text-sm"
          >
            ← Retour
          </button>

          <div className="text-center mb-4">
            <h1 className="text-2xl font-bold text-amber-50 font-display mb-1">🎓 Espace Élève</h1>
            <p className="text-slate-400 text-sm">Entre le code donné par ton professeur</p>
          </div>

          {verificationEmailSent && (
            <div className="bg-green-900/40 border border-green-500/50 text-white px-3 py-2 rounded-xl mb-3 text-xs">
              Compte créé ! Un email de vérification a été envoyé à <strong>{email}</strong>. Clique sur le lien avant de te connecter.
            </div>
          )}

          {error && (
            <div className="bg-red-900/40 border border-red-500/50 text-white px-3 py-2 rounded-xl mb-3 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleStudentSubmit}>
            <div className="mb-3">
              <label htmlFor="studentCode" className="block text-slate-300 font-semibold mb-1 text-sm">
                Code d'accès
              </label>
              <input
                type="text"
                id="studentCode"
                value={studentCode}
                onChange={(e) => setStudentCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-2 bg-slate-900/60 border border-white/10 rounded-xl text-center font-mono text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="XXXXX"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="student-email" className="block text-slate-300 font-semibold mb-1 text-sm">
                Ton adresse email
              </label>
              <input
                type="email"
                id="student-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                placeholder="ton.email@exemple.com"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="student-password" className="block text-slate-300 font-semibold mb-1 text-sm">
                Mot de passe <span className="text-slate-300 font-normal">(min. 6 car.)</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="student-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-12 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-amber-50 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {renderConfirmPasswordField('student-password-confirm')}

            <AgeConsentCheckbox />

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2.5 rounded-full font-semibold text-white transition-all ${
                loading
                  ? 'bg-slate-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:shadow-lg'
              }`}
            >
              {loading ? 'Création du compte...' : 'Créer mon compte élève'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Formulaire mode professeur
  if (mode === 'teacher') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden p-4">
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-amber-500/10 to-transparent pointer-events-none" />
        <div className="bg-slate-800/60 backdrop-blur-xl border border-white/10 p-8 rounded-2xl shadow-2xl w-full max-w-md relative z-10">
          <button
            onClick={() => setMode('choose')}
            className="mb-4 text-slate-400 hover:text-amber-50 flex items-center gap-2 transition-colors"
          >
            ← Retour
          </button>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-amber-50 font-display mb-2">👨‍🏫 Espace Professeur</h1>
            <p className="text-slate-400">Créez votre compte pour gérer vos élèves</p>
          </div>

          {verificationEmailSent && (
            <div className="bg-green-900/40 border border-green-500/50 text-white px-4 py-3 rounded-xl mb-4 text-sm">
              Compte créé ! Un email de vérification a été envoyé à <strong>{email}</strong>. Cliquez sur le lien pour activer votre compte, puis connectez-vous.
            </div>
          )}

          {error && (
            <div className="bg-red-900/40 border border-red-500/50 text-white px-4 py-3 rounded-xl mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleEmailSubmit}>
            <div className="mb-4">
              <label htmlFor="teacherCode" className="block text-slate-300 font-semibold mb-2">
                Code d'accès professeur
              </label>
              <input
                type="text"
                id="teacherCode"
                value={teacherCode}
                onChange={(e) => setTeacherCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 bg-slate-900/60 border border-white/10 rounded-xl text-center text-lg font-mono text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="XXXXX"
                required
                disabled={loading}
              />
              <p className="text-xs text-slate-500 mt-1">Code fourni par Cyril</p>
            </div>

            <div className="mb-4">
              <label htmlFor="email-teacher" className="block text-slate-300 font-semibold mb-2">
                Email
              </label>
              <input
                type="email"
                id="email-teacher"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="votre.email@exemple.com"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-6">
              <label htmlFor="password-teacher" className="block text-slate-300 font-semibold mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password-teacher"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-12 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-amber-50 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Minimum 6 caractères</p>
            </div>

            {renderConfirmPasswordField('password-teacher-confirm', 'mb-6')}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-full font-semibold text-white transition-all text-lg ${
                loading
                  ? 'bg-slate-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:shadow-lg'
              }`}
            >
              {loading ? 'Création du compte...' : 'Créer mon compte professeur'}
            </button>
          </form>

          <div className="mt-6 bg-amber-900/20 border border-amber-500/30 rounded-xl p-4">
            <p className="text-sm text-white font-semibold mb-2">✨ Compte professeur :</p>
            <ul className="text-xs text-slate-200 space-y-1">
              <li>• Code unique pour vos élèves</li>
              <li>• Tableau de bord avec statistiques</li>
              <li>• Mode cours pour jouer avec un élève</li>
              <li>• Suivi de la progression de chaque élève</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire mode email (connexion ou inscription)
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden p-4">
      <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-amber-500/10 to-transparent pointer-events-none" />
      <div className="bg-slate-800/60 backdrop-blur-xl border border-white/10 p-8 rounded-2xl shadow-2xl w-full max-w-md relative z-10">
        <button
          onClick={() => setMode('choose')}
          className="mb-4 text-slate-400 hover:text-amber-50 flex items-center gap-2 transition-colors"
        >
          ← Retour
        </button>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-amber-50 font-display mb-2">
            {mode === 'login' ? 'Connexion' : 'Inscription'}
          </h1>
          <p className="text-slate-400">
            {mode === 'login' ? 'Connecte-toi à ton compte' : 'Crée ton compte avec email'}
          </p>
        </div>

        {emailJustVerified && (
          <div className="bg-green-900/40 border border-green-500/50 text-white px-4 py-3 rounded-xl mb-4 text-sm">
            ✅ Email vérifié ! Tu peux maintenant te connecter.
          </div>
        )}

        {verificationError && (
          <div className="bg-red-900/40 border border-red-500/50 text-white px-4 py-3 rounded-xl mb-4 text-sm">
            Le lien de vérification est invalide ou expiré. Crée un nouveau compte ou renvoie l'email.
          </div>
        )}

        {error && (
          <div className="bg-red-900/40 border border-red-500/50 text-white px-4 py-3 rounded-xl mb-4">
            {error}
            {notVerifiedEmail && (
              <button
                type="button"
                className="mt-2 block text-xs underline text-white hover:text-slate-200"
                onClick={async () => {
                  try {
                    await resendVerificationEmail(notVerifiedEmail, password);
                    setError('');
                    setVerificationEmailSent(true);
                  } catch {
                    setError('Impossible de renvoyer l\'email. Vérifie ton mot de passe.');
                  }
                }}
              >
                Renvoyer l'email de vérification
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleEmailSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-slate-300 font-semibold mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              placeholder="votre.email@exemple.com"
              required
              disabled={loading}
            />
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="block text-slate-300 font-semibold mb-2">
              Mot de passe
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 pr-12 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="••••••••"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-amber-50 focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
            {mode === 'register' && (
              <p className="text-xs text-slate-300 mt-1">Minimum 6 caractères</p>
            )}
            {mode === 'login' && (
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                className="mt-2 text-xs text-amber-400 hover:text-amber-300 transition-colors"
              >
                Mot de passe oublié ?
              </button>
            )}
          </div>

          {mode === 'register' && renderConfirmPasswordField('password-confirm', 'mb-6')}

          {verificationEmailSent && (
            <div className="bg-green-900/40 border border-green-500/50 text-white px-4 py-3 rounded-xl mb-4 text-sm">
              Email de vérification envoyé ! Vérifie ta boîte mail puis connecte-toi.
            </div>
          )}

          {resetEmailSent && (
            <div className="bg-green-900/40 border border-green-500/50 text-white px-4 py-3 rounded-xl mb-4 text-sm">
              Email de réinitialisation envoyé ! Vérifie ta boîte mail.
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-full font-semibold text-white transition-all ${
              loading
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:shadow-lg'
            }`}
          >
            {loading
              ? (mode === 'register' ? 'Création du compte...' : 'Connexion...')
              : (mode === 'register' ? 'Créer mon compte' : 'Se connecter')
            }
          </button>
        </form>

        {mode === 'register' && (
          <div className="mt-6 bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
            <p className="text-sm text-blue-300 font-semibold mb-2">Après inscription :</p>
            <p className="text-xs text-blue-300/70">
              Tu seras invité à choisir un pseudo et renseigner tes informations pour participer aux classements
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
